/**
 * Zentrale Polling-Engine (client.md §7). Alle React-Query-Queries hier definiert —
 * niemand sonst ruft das API zyklisch auf. `refetchIntervalInBackground: false` pausiert
 * automatisch, sobald der Tab versteckt ist (document.visibilitychange).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import * as api from "../api/client";
import type { ApiCtx } from "../api/client";
import type { StatusResponse } from "../api/types";
import { parseStatusMarkdown, type ParsedStatus } from "../parse/status";
import { useWorkspaces } from "../state/workspaces";
import { notifyOpenQuestion, notifyTerminalState } from "../notify";

export type WorkspaceStateKey = "ready" | "planned" | "running" | "waiting" | "budget" | "done" | "error";

/** Badge-Ableitung aus /status allein — "waiting" (offene Frage) und "planned" (Plan bereit,
 * noch nicht gestartet) kommen aus den Decisions- bzw. Plan-Daten dazu (siehe combineState). */
export function deriveStateFromStatus(status: StatusResponse, parsed: ParsedStatus): WorkspaceStateKey {
  const run = status.run;
  if (run?.state === "error") return "error";
  if (run?.state === "running" || run?.state === "queued") return "running";
  const last = parsed.history[parsed.history.length - 1];
  if (last && /budget-exhausted/.test(last.text)) return "budget";
  if (last && /stopped:\s*done/.test(last.text)) return "done";
  return "ready";
}

export function combineState(
  base: WorkspaceStateKey,
  hasOpenDecision: boolean,
  planReadyNotStarted: boolean,
): WorkspaceStateKey {
  if (base === "running" || base === "error" || base === "done" || base === "budget") return base;
  if (hasOpenDecision) return "waiting";
  if (planReadyNotStarted) return "planned";
  return base;
}

export function useWorkspaceStatusQuery(ctx: ApiCtx | null, pollSeconds: number) {
  const setLastKnown = useWorkspaces((s) => s.setLastKnown);
  const query = useQuery({
    queryKey: ["status", ctx?.apiUrl, ctx?.token, ctx?.workspace],
    queryFn: () => api.status(ctx as ApiCtx),
    enabled: !!ctx,
    refetchIntervalInBackground: false,
    refetchInterval: (q) => {
      const data = q.state.data as StatusResponse | undefined;
      const active = data?.run?.state === "queued" || data?.run?.state === "running";
      return (active ? pollSeconds : 30) * 1000;
    },
  });

  const parsed = query.data ? parseStatusMarkdown(query.data.status_markdown ?? "") : undefined;

  useEffect(() => {
    if (!ctx || !query.data || query.data.code < 0 || !parsed) return;
    const state = deriveStateFromStatus(query.data, parsed);
    setLastKnown(ctx.workspace, {
      state,
      detail: query.data.run?.detail,
      updatedAt: new Date().toISOString(),
      budget: query.data.budget
        ? { total: query.data.budget.total, used: query.data.budget.used }
        : undefined,
    });
    if (state === "budget") {
      notifyTerminalState(
        ctx.workspace,
        query.data.run?.run_id,
        "budget",
        `Budget erschöpft: ${query.data.budget?.used ?? "?"} von ${query.data.budget?.total ?? "?"} Tokens.`,
      );
    } else if (state === "done") {
      notifyTerminalState(ctx.workspace, query.data.run?.run_id, "done", "Ziel erreicht — Ergebnisse liegen bereit.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.workspace, query.data]);

  return { ...query, parsed };
}

export function useWorkspaceDecisionsQuery(ctx: ApiCtx | null, active: boolean) {
  const query = useQuery({
    queryKey: ["decisions", ctx?.apiUrl, ctx?.token, ctx?.workspace],
    queryFn: () => api.decisions(ctx as ApiCtx),
    enabled: !!ctx,
    refetchIntervalInBackground: false,
    refetchInterval: active ? 15_000 : false,
  });

  useEffect(() => {
    if (!ctx || !query.data || query.data.code < 0) return;
    for (const d of query.data.decisions ?? []) {
      if (!d.answer) notifyOpenQuestion(ctx.workspace, d.qid, d.question);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.workspace, query.data]);

  return query;
}

export function useWorkspaceFilesQuery(ctx: ApiCtx | null, statusChangedAt: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["files", ctx?.apiUrl, ctx?.token, ctx?.workspace],
    queryFn: () => api.files(ctx as ApiCtx),
    enabled: !!ctx,
  });
  const lastSeen = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!ctx || statusChangedAt === undefined || lastSeen.current === statusChangedAt) return;
    lastSeen.current = statusChangedAt;
    queryClient.invalidateQueries({ queryKey: ["files", ctx.apiUrl, ctx.token, ctx.workspace] });
  }, [ctx, statusChangedAt, queryClient]);
  return query;
}

export function useWorkspaceLogsQuery(ctx: ApiCtx | null, statusChangedAt: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["logs", ctx?.apiUrl, ctx?.token, ctx?.workspace],
    queryFn: () => api.logs(ctx as ApiCtx),
    enabled: !!ctx,
  });
  const lastSeen = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!ctx || statusChangedAt === undefined || lastSeen.current === statusChangedAt) return;
    lastSeen.current = statusChangedAt;
    queryClient.invalidateQueries({ queryKey: ["logs", ctx.apiUrl, ctx.token, ctx.workspace] });
  }, [ctx, statusChangedAt, queryClient]);
  return query;
}

/** Startseite: status(w) aller bekannten Arbeitsbereiche, sequenziell alle 60 s (§7). */
export function useAllWorkspacesStatusPoll(apiUrl: string, active: boolean) {
  const order = useWorkspaces((s) => s.order);
  const byName = useWorkspaces((s) => s.byName);
  const setLastKnown = useWorkspaces((s) => s.setLastKnown);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    async function pollOnce() {
      for (const name of order) {
        if (cancelled) return;
        const entry = byName[name];
        if (!entry) continue;
        try {
          const res = await api.status({ apiUrl, token: entry.token, workspace: name });
          if (cancelled || res.code < 0) continue;
          const parsed = parseStatusMarkdown(res.status_markdown ?? "");
          setLastKnown(name, {
            state: deriveStateFromStatus(res, parsed),
            detail: res.run?.detail,
            updatedAt: new Date().toISOString(),
            budget: res.budget ? { total: res.budget.total, used: res.budget.used } : undefined,
          });
        } catch {
          // Netzwerkfehler: lastKnown bleibt wie zuletzt bekannt, kein Absturz (§13).
        }
      }
    }

    void pollOnce();
    const id = window.setInterval(() => void pollOnce(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, active, order.join(",")]);
}
